// @flow
import AccountBoxRoundedIcon from '@material-ui/icons/AccountBoxRounded';
import AccountCircleRoundedIcon from '@material-ui/icons/AccountCircleRounded';
import AccountTreeRoundedIcon from '@material-ui/icons/AccountTreeRounded';
import AllInboxRoundedIcon from '@material-ui/icons/AllInboxRounded';
import BusinessRoundedIcon from '@material-ui/icons/BusinessRounded';
import ContactsIcon from '@material-ui/icons/Contacts';
import DashboardIcon from '@material-ui/icons/Dashboard';
import DescriptionIcon from '@material-ui/icons/Description';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import DvrRounded from '@material-ui/icons/DvrRounded';
import EventAvailableRounded from '@material-ui/icons/EventAvailableRounded';
import FindInPageRoundedIcon from '@material-ui/icons/FindInPageRounded';
import ForumRoundedIcon from '@material-ui/icons/ForumRounded';
import GroupRoundedIcon from '@material-ui/icons/GroupRounded';
import GroupWorkRoundedIcon from '@material-ui/icons/GroupWorkRounded';
import HelpRoundedIcon from '@material-ui/icons/HelpRounded';
import HomeRoundedIcon from '@material-ui/icons/HomeRounded';
import InsertDriveFileRoundedIcon from '@material-ui/icons/InsertDriveFileRounded';
import LanguageRoundedIcon from '@material-ui/icons/LanguageRounded';
import LocalAtmRoundedIcon from '@material-ui/icons/LocalAtmRounded';
import MonetizationOnRoundedIcon from '@material-ui/icons/MonetizationOnRounded';
import PageviewRoundedIcon from '@material-ui/icons/PageviewRounded';
import PermPhoneMsgRoundedIcon from '@material-ui/icons/PermPhoneMsgRounded';
import PersonPinRoundedIcon from '@material-ui/icons/PersonPinRounded';
import PieChartIcon from '@material-ui/icons/PieChart';
import PlayCircleOutlineRoundedIcon from '@material-ui/icons/PlayCircleOutlineRounded';
import PlaylistAddCheck from '@material-ui/icons/PlaylistAddCheck';
import RecordVoiceOverRoundedIcon from '@material-ui/icons/RecordVoiceOverRounded';
import RoomRoundedIcon from '@material-ui/icons/RoomRounded';
import SchoolRoundedIcon from '@material-ui/icons/SchoolRounded';
import SmsIcon from '@material-ui/icons/Sms';
import StoreMallDirectoryRoundedIcon from '@material-ui/icons/StoreMallDirectoryRounded';
import SupervisedUserCircleRoundedIcon from '@material-ui/icons/SupervisedUserCircleRounded';
import Timeline from '@material-ui/icons/Timeline';
import WorkRoundedIcon from '@material-ui/icons/WorkRounded';
import { EntityRoutes } from 'routes/constants';
import { getCurrentUser } from 'services/Authentication';
import { Permissions, userHasPermission, userHasRoles } from 'services/Authorization';
import {
  CALL_VERBIAGES_URL,
  COURSES_TRAININGS_URL,
  HELP_ARTICLES_URL,
  TALENT_POOL_URL
} from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { hasFeatureFlag } from 'UI/utils';
import { isFeatureAccessible } from 'utils/featureAccesible';

/**
 * @typedef subItems
 * @property {String} title
 * @property {String} route
 * @property {String} [featureFlag]
 * @property {IconItem} icon
 */
/**
 * {subItems[]}
 */

const subItems = {
  commandCenter: [
    {
      title: 'Daily Tracker',
      route: EntityRoutes.Home,
      icon: HomeRoundedIcon
    },
    {
      title: 'Overview',
      route: EntityRoutes.CommandCenterOverview,
      icon: DashboardIcon,
      permission: Permissions.CommandCenter.Overview.CanUseModule
    },
    {
      title: 'Dominate Sheet',
      route: EntityRoutes.CommandCenterDominateSheet,
      icon: DescriptionIcon,
      permission: Permissions.CommandCenter.DominateSheet.CanUseModule
    },
    {
      title: 'Tools',
      route: '/tools',
      icon: PlaylistAddCheck,
      subItems: [
        {
          title: 'Tools Usage',
          route: EntityRoutes.DashboardToolsUsageReport,
          featureFlag: FeatureFlags.DashboardToolsUsageReport
        },
        {
          title: 'Calls & Texts',
          route: EntityRoutes.DashboardPhone
        },
        {
          title: 'Bulks',
          route: EntityRoutes.DashboardBulkEmail
        }
      ]
    },
    {
      title: 'Productivity',
      route: '/productivity',
      icon: DvrRounded,
      subItems: [
        {
          title: 'Sendouts',
          route: EntityRoutes.DashboardSendout,
          featureFlag: FeatureFlags.DashboardSendouts
        },
        {
          title: 'Sendouts Leaderboard',
          route: EntityRoutes.DashboardSendoutLeaders,
          featureFlag: FeatureFlags.DashboardSendoutLeaders
        },
        {
          title: 'Inventory',
          route: EntityRoutes.DashboardOverview
        },
        {
          title: 'Fee Agreements',
          route: EntityRoutes.DashboardFeeAgreements,
          featureFlag: FeatureFlags.DashboardFeeAgreements
        }
      ]
    },
    {
      title: 'Financial Boards',
      route: '/financial',
      icon: PieChartIcon,
      subItems: [
        {
          title: 'Production',
          route: EntityRoutes.CommandCenterProductionBoard,
          featureFlag: FeatureFlags.CommandCenterProductionBoardPage,
          roles: [
            Roles.Coach,
            Roles.RegionalDirector,
            Roles.ProductionDirector,
            Roles.Operations,
            Roles.DataCoordinator,
            Roles.Finance,
            Roles.Staff,
            Roles.Leadership,
            Roles.AssistantRegionalDirector
          ]
        },
        {
          title: 'Fall Offs',
          route: EntityRoutes.CommandCenterFallOffsBoard,
          featureFlag: FeatureFlags.CommandCenterFallOffsBoard,
          roles: [
            Roles.Coach,
            Roles.RegionalDirector,
            Roles.ProductionDirector,
            Roles.Operations,
            Roles.DataCoordinator,
            Roles.Finance,
            Roles.Staff,
            Roles.Leadership,
            Roles.AssistantRegionalDirector
          ]
        },
        {
          title: 'Commissions',
          route: EntityRoutes.CommandCenterCommissionsBoard,
          featureFlag: FeatureFlags.CommandCenterCommissionsBoard
        }
      ]
    },

    {
      title: 'Overall Performance',
      route: EntityRoutes.CommandCenterOverallPerformance,
      icon: Timeline,
      permission: Permissions.CommandCenter.Activity.CanUseModule
    },

    {
      title: 'Daily Performance',
      route: EntityRoutes.CommandCenterDailyPerformance,
      icon: InsertDriveFileRoundedIcon,
      showWhen: () =>
        userHasPermission(Permissions.CommandCenter.DailyPerformance.CanUseModule) ||
        userHasRoles([Roles.Recruiter])
    }
  ],
  directory: [
    {
      title: 'Contacts',
      route: EntityRoutes.Contacts,
      icon: AccountCircleRoundedIcon
    },
    {
      title: 'Companies',
      route: EntityRoutes.Companies,
      icon: BusinessRoundedIcon
    }
  ],
  inventory: [
    {
      title: 'Candidates',
      route: EntityRoutes.Candidates,
      icon: PersonPinRoundedIcon
    },
    {
      title: 'Job Orders',
      route: EntityRoutes.JobOrders,
      icon: WorkRoundedIcon
    },
    {
      title: 'Hot Sheet',
      route: EntityRoutes.Sendouts,
      permission: Permissions.Sendouts.CanUseModule,
      icon: ForumRoundedIcon
    },
    {
      title: 'Placements',
      route: EntityRoutes.Placements,
      permission: Permissions.Placements.CanUseModule,
      icon: MonetizationOnRoundedIcon
    },
    {
      title: 'AR & Collections',
      route: EntityRoutes.Collections,
      roles: [Roles.Coach, Roles.Finance, Roles.Leadership, Roles.AssistantRegionalDirector],
      featureFlag: FeatureFlags.PlacementCollections,
      icon: LocalAtmRoundedIcon
    },
    {
      title: 'Channel Partner HS',
      route: EntityRoutes.SendoutsFromChannelPartner,
      permission: Permissions.Sendouts.CanUseModule,
      featureFlag: FeatureFlags.SendoutsChannelPartner,
      icon: AccountTreeRoundedIcon
    },
    {
      title: 'Resume Search',
      route: EntityRoutes.ResumeSearch,
      showWhen: () =>
        isFeatureAccessible({
          checkIfFeatureFlagEnabled: hasFeatureFlag,
          featureFlag: FeatureFlags.ResumeSearch,
          permission: Permissions.ResumeSearch.CanUseModule,
          user: getCurrentUser()
        }),
      icon: InsertDriveFileRoundedIcon
    }
  ],
  pil: [
    {
      title: 'Map',
      route: EntityRoutes.Map,
      icon: RoomRoundedIcon
    },
    {
      title: 'Industries',
      route: EntityRoutes.Industries,
      featureFlag: FeatureFlags.IndustriesViewer,
      icon: StoreMallDirectoryRoundedIcon
    },
    {
      title: 'Teams',
      route: EntityRoutes.Teams,
      featureFlag: FeatureFlags.IndustriesViewer,
      icon: SupervisedUserCircleRoundedIcon
    }
  ],
  trainingAndHelp: [
    {
      title: 'Courses & Trainings',
      url: COURSES_TRAININGS_URL,
      icon: PlayCircleOutlineRoundedIcon
    },
    {
      title: 'Help & Information',
      url: HELP_ARTICLES_URL,
      icon: HelpRoundedIcon
    },
    {
      title: 'AI Roleplay',
      route: EntityRoutes.Roleplay,
      icon: RecordVoiceOverRoundedIcon
    },
    {
      title: 'Call Verbiages',
      url: CALL_VERBIAGES_URL,
      icon: PermPhoneMsgRoundedIcon
    }
  ]
};

/**
 * @typedef SidebarItem
 * @property {String} title
 * @property {String} route
 * @property {String} [permission]
 * @property {String} [featureFlag]
 * @property {Array} subItems
 * @property {Number} position
 * @property {IconItem} icon
 */

/**
 * {SidebarItem[]}
 */
const sideBarMenu = [
  {
    title: 'Command Center',
    route: EntityRoutes.CommandCenter,
    position: 1,
    icon: LanguageRoundedIcon,
    subItems: subItems.commandCenter
  },
  {
    title: 'Training & Help',
    route: 'trainig',
    position: 2,
    icon: SchoolRoundedIcon,
    subItems: subItems.trainingAndHelp,
    featureFlag: FeatureFlags.AIRoleplay,
    permission: Permissions.AIIntegrations.Roleplay
  },
  {
    title: 'Daily Plan',
    route: EntityRoutes.DailyPlan,
    position: 3,
    icon: EventAvailableRounded,
    featureFlag: FeatureFlags.DailyPlan,
    permission: Permissions.DailyPlan.CanUseModule
  },
  {
    title: 'Directory',
    route: 'directory',
    position: 4,
    icon: AccountBoxRoundedIcon,
    subItems: subItems.directory
  },
  {
    title: 'Inventory',
    route: 'inventory',
    position: 5,
    icon: GroupRoundedIcon,
    subItems: subItems.inventory
  },
  {
    title: 'Talent Pool',
    url: TALENT_POOL_URL,
    position: 6,
    icon: ContactsIcon,
    featureFlag: FeatureFlags.TalentPoolLink
  },
  {
    title: 'Search Projects',
    route: EntityRoutes.SearchProjects,
    position: 7,
    icon: FindInPageRoundedIcon
  },
  {
    title: 'Text Messages',
    route: EntityRoutes.MessageCenter,
    featureFlag: FeatureFlags.MessageCenter,
    permission: Permissions.Textel.CanUseModule,
    position: 8,
    icon: SmsIcon
  },
  {
    title: 'Bulks',
    route: EntityRoutes.BulkEmail,
    position: 9,
    icon: AllInboxRoundedIcon
  },
  {
    title: 'Fee Agreements',
    route: EntityRoutes.FeeAgreements,
    position: 10,
    icon: DescriptionRoundedIcon
  },
  {
    title: 'PIL',
    route: 'pil',
    position: 11,
    icon: PageviewRoundedIcon,
    subItems: subItems.pil
  },
  {
    title: 'Roster',
    route: EntityRoutes.Roster,
    position: 12,
    icon: GroupWorkRoundedIcon
  }
];

export default sideBarMenu.sort((a, b) => a.position - b.position);
